/**
 * Utility functions for transforming train data
 * These functions convert API response data to the format expected by UI components
 */

/**
 * Convert 24-hour time format to 12-hour format with separate meridiem
 * @param {string} time24 - Time in 24-hour format (e.g., "14:30")
 * @returns {Object} Object with time12h and meridiem properties
 */
export const convertTo12HourFormat = (time24) => {
    try {
        if (!time24 || typeof time24 !== 'string') return { time12h: null, meridiem: null };
        
        const parts = time24.split(':');
        if (parts.length !== 2) return { time12h: null, meridiem: null };
        
        const [hours, minutes] = parts;
        if (!hours || !minutes) return { time12h: null, meridiem: null };
        
        const hour = parseInt(hours, 10);
        if (isNaN(hour)) return { time12h: null, meridiem: null };
        
        const meridiem = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        const time12h = `${hour12}:${minutes}`;
        
        return { time12h, meridiem };
    } catch (error) {
        console.error('Error in convertTo12HourFormat:', error);
        return { time12h: null, meridiem: null };
    }
};

/**
 * Convert total minutes to hours and minutes
 * @param {number} totalMinutes - Total minutes (e.g., 90)
 * @returns {Object} Object with hours and minutes properties
 */
export const convertMinutesToHoursAndMinutes = (totalMinutes) => {
    try {
        if (typeof totalMinutes !== 'number' || isNaN(totalMinutes)) {
            return { hours: 0, minutes: 0 };
        }
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return { hours, minutes };
    } catch (error) {
        console.error('Error in convertMinutesToHoursAndMinutes:', error);
        return { hours: 0, minutes: 0 };
    }
};

/**
 * Get day name from epoch timestamp in seconds
 * @param {number} epochInSeconds - Epoch timestamp in seconds
 * @returns {string} Day name (e.g., "Mon", "Tue")
 */
export const getDayNameFromEpoch = (epochInSeconds) => {
    try {
        if (!epochInSeconds || typeof epochInSeconds !== 'number') return '';
        
        const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const date = new Date(epochInSeconds * 1000);
        const day = date.getDay();
        
        if (isNaN(day) || day < 0 || day > 6) return '';
        
        return daysOfWeek[day];
    } catch (error) {
        console.error('Error in getDayNameFromEpoch:', error);
        return '';
    }
};

/**
 * Transform train data to include 12-hour format fields that UI expects
 * @param {Object} train - Train data from API
 * @returns {Object} Transformed train data with additional UI-friendly fields
 */
export const transformTrainData = (train) => {
    try {
        if (!train || typeof train !== 'object') return train;
        
        const departureParsed = convertTo12HourFormat(train.departureTime);
        const arrivalParsed = convertTo12HourFormat(train.arrivalTime);
        const durationParsed = convertMinutesToHoursAndMinutes(train.duration);
        
        // Get day names from epoch timestamps
        const departureDay = getDayNameFromEpoch(train.departureTimeEpochInSec);
        const arrivalDay = getDayNameFromEpoch(train.arrivalTimeEpochInSec);
        
        return {
            ...train,
            departureTime12hFormat: departureParsed?.time12h || null,
            departureMeridiem: departureParsed?.meridiem || null,
            arrivalTime12hFormat: arrivalParsed?.time12h || null,
            arrivalMeridiem: arrivalParsed?.meridiem || null,
            durationHours: durationParsed?.hours || 0,
            durationMinutes: durationParsed?.minutes || 0,
            fromDay: departureDay || '',
            toDay: arrivalDay || '',
        };
    } catch (error) {
        console.error('Error in transformTrainData:', error);
        return train;
    }
};